import { foldStore, snapshotOf, type Id, type OrderEvent, type StoreModel } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { getDeviceId } from '../db/clock';
import { commit } from '../db/commit';
import { db, getMeta, setMeta } from '../db/db';
import { aiCall, aiName } from './ai';
import { forgetPlacementTries, triedKey } from './placementTries';
import { buildMergeMap, resolveWith } from './products';
import { loadStoreModel } from './shopping';

/** Tyle produktów musi znać sklep, żeby jego trasa coś mówiła (AI: z kopiami; źródło kopii dla sieci: tylko potwierdzone). */
const MIN_KNOWN = 8;
/** Produktu, którego AI nie umiało umieścić, nie pytamy ponownie przez tydzień. */
const TRIED_TTL_MS = 7 * 24 * 60 * 60_000;
/** Najwyżej jedno pytanie o miejsca na sklep w tym czasie. */
const STORE_THROTTLE_MS = 5 * 60_000;
// Limity /api/ai/place.
const MAX_ROUTE = 500;
const MAX_ITEMS = 60;

/** Pozycje potwierdzone wizytą (bez skopiowanych z innego sklepu i zgadniętych). */
export const confirmedCount = (model: StoreModel) => Object.values(model.byProduct).filter((p) => !p.seeded && !p.guessed).length;

/** Pozycje bez zgadniętych — skopiowana trasa wystarcza AI za punkt odniesienia już przy pierwszej wizycie. */
const knownCount = (model: StoreModel) => Object.values(model.byProduct).filter((p) => !p.guessed).length;

/** Model ma pozycje, ale żadnej potwierdzonej, a trasa pochodzi z kopii — „Trasa skopiowana z …”. */
export const isCopiedRoute = (model: StoreModel) =>
  !!model.seededFrom && Object.keys(model.byProduct).length > 0 && confirmedCount(model) === 0;

/** Trasa dla AI: produkty modelu w kolejności sklepu; dłuższą próbkujemy równomiernie, żeby objąć cały sklep. */
function routeOf(model: StoreModel, max = MAX_ROUTE): Id[] {
  const ids = Object.entries(model.byProduct)
    .sort(([a, pa], [b, pb]) => pa.pos - pb.pos || (a < b ? -1 : a > b ? 1 : 0))
    .map(([id]) => id);
  if (ids.length <= max) return ids;
  return Array.from({ length: max }, (_, i) => ids[Math.round((i * (ids.length - 1)) / (max - 1))]!);
}

/**
 * Kilka produktów za tym samym sąsiadem: każdy kolejny (w kolejności AI) staje za poprzednim z nich,
 * inaczej wszystkie lądowałyby w tym samym środku przed następnikiem i w odwrotnej kolejności.
 */
export function chainPlacements(placements: readonly { id: Id; afterId: Id | null }[]): { productId: Id; afterId: Id | null }[] {
  const last = new Map<Id | null, Id>();
  return placements.map((p) => {
    const afterId = last.get(p.afterId) ?? p.afterId;
    last.set(p.afterId, p.id);
    return { productId: p.id, afterId };
  });
}

let placing: Promise<void> | null = null;
const lastPlaceMs = new Map<Id, number>();

/**
 * Zgaduje przez AI miejsca na trasie dla produktów z aktywnych list, których sklep jeszcze nie zna.
 * Wołane po każdej synchronizacji: lista przygotowana w domu ma miejsca policzone, zanim w sklepie zabraknie zasięgu.
 * Wynik to jedno zdarzenie `placed` na sklep — zgadywanka nie wpływa na naukę i znika po pierwszym odhaczeniu.
 */
export function ensurePlacements(): Promise<void> {
  if (placing || !navigator.onLine) return placing ?? Promise.resolve();
  placing = placeAll()
    .catch((e: unknown) => console.warn('Miejsca z AI', e))
    .finally(() => {
      placing = null;
    });
  return placing;
}

async function placeAll(): Promise<void> {
  const [lists, stores, products, chains] = await Promise.all([
    db.lists.where('status').equals('active').toArray(),
    db.stores.toArray(),
    db.products.toArray(),
    db.chains.toArray(),
  ]);
  const resolveStore = resolveWith(buildMergeMap(stores));
  const resolveProduct = resolveWith(buildMergeMap(products));
  const storeById = new Map(stores.map((s) => [s.id, s]));
  const productName = new Map(products.filter((p) => aiName(p.name)).map((p) => [p.id, aiName(p.name)]));

  // Sklep → produkty z pozycji do kupienia na jego aktywnych listach.
  const wanted = new Map<Id, Set<Id>>();
  for (const l of lists) {
    if (l.deleted || !l.storeId) continue;
    const storeId = resolveStore(l.storeId);
    const store = storeById.get(storeId);
    if (!store || store.deleted) continue;
    const items = await db.listItems.where('listId').equals(l.id).toArray();
    for (const i of items) {
      if (i.deleted || i.status !== 'pending' || !i.productId) continue;
      const set = wanted.get(storeId) ?? new Set<Id>();
      set.add(resolveProduct(i.productId));
      wanted.set(storeId, set);
    }
  }

  const chainName = new Map(chains.map((c) => [c.id, c.name]));
  const resolveChain = resolveWith(buildMergeMap(chains));
  for (const [storeId, productIds] of wanted) {
    const now = Date.now();
    if (now - (lastPlaceMs.get(storeId) ?? 0) < STORE_THROTTLE_MS) continue;
    const model = await loadStoreModel(storeId);
    if (knownCount(model) < MIN_KNOWN) continue;
    const tried = Object.fromEntries(
      Object.entries((await getMeta<Record<Id, number>>(triedKey(storeId))) ?? {}).filter(([, ms]) => now - ms < TRIED_TTL_MS),
    );
    const candidates = [...productIds]
      .filter((id) => !Object.hasOwn(model.byProduct, id) && !Object.hasOwn(tried, id) && productName.has(id))
      .sort()
      .slice(0, MAX_ITEMS);
    if (candidates.length === 0) continue;

    const store = storeById.get(storeId)!;
    const route = routeOf(model).filter((id) => productName.has(id));
    if (route.length === 0) continue;
    lastPlaceMs.set(storeId, now);
    const res = await aiCall<{ placements?: unknown }>('/api/ai/place', {
      storeName: aiName(store.name) || 'Sklep',
      chainName: (store.chainId && aiName(chainName.get(resolveChain(store.chainId)))) || null,
      route: route.map((id) => ({ id, name: productName.get(id)! })),
      items: candidates.map((id) => ({ id, name: productName.get(id)! })),
    });
    // AI niedostępne — spróbujemy przy kolejnej synchronizacji (po przerwie dla sklepu).
    if (!res || !Array.isArray(res.placements)) continue;

    const asked = new Set(candidates);
    const onRoute = new Set(route);
    const seen = new Set<Id>();
    const valid: { id: Id; afterId: Id | null }[] = [];
    for (const p of res.placements as { id?: unknown; afterId?: unknown }[]) {
      if (typeof p?.id !== 'string' || !asked.has(p.id) || seen.has(p.id)) continue;
      if (p.afterId !== null && (typeof p.afterId !== 'string' || !onRoute.has(p.afterId))) continue;
      seen.add(p.id);
      valid.push({ id: p.id, afterId: p.afterId });
    }
    const placements = chainPlacements(valid);

    // Próbowane zapamiętujemy także wtedy, gdy AI nic nie umieściło.
    for (const id of candidates) tried[id] = now;
    if (placements.length === 0) {
      await setMeta(triedKey(storeId), tried);
      continue;
    }
    await commit(async (w) => {
      await setMeta(triedKey(storeId), tried);
      await w.append({ storeId, kind: 'placed', source: 'ai', placements, deviceId: getDeviceId() });
    });
  }
}

const autoSeeding = new Set<Id>();

/**
 * Nowy sklep sieci dostaje od razu trasę innego sklepu tej sieci (działa offline).
 * Zdarzenie `seeded` z ifEmpty działa tylko na pustej trasie, więc dwa telefony naraz albo późno
 * dotarła wizyta nic nie psują. Po „Cofnij” (reset) sklep ma już zdarzenia, więc nic nie kopiuje się ponownie.
 */
export async function autoSeed(storeId: Id): Promise<void> {
  // StrictMode i ponowne wejście w tryb sklepu — jeden przebieg naraz na sklep.
  if (autoSeeding.has(storeId)) return;
  autoSeeding.add(storeId);
  try {
    // Nowe urządzenie przed pierwszym pullem nie zna historii — mogłoby skopiować trasę sklepu, który już ją ma.
    if (((await getMeta<number>('cursor')) ?? 0) <= 0) return;
    const source = await seedSourceFor(storeId);
    if (!source) return;
    const { target, model } = source;
    await commit(async (w) => {
      // Ponownie w transakcji: zdarzenie mogło właśnie przyjść z synchronizacji.
      if ((await loadStoreModel(target)).eventCount > 0) return;
      await w.append({ storeId: target, kind: 'seeded', fromStoreId: model.storeId, snapshot: snapshotOf(model), ifEmpty: true, deviceId: getDeviceId() });
    });
  } catch (e) {
    console.warn('Kopia trasy z sieci', e);
  } finally {
    autoSeeding.delete(storeId);
  }
}

/** Sklep sieci, z którego skopiujemy trasę: najwięcej wizyt, potem więcej potwierdzonych produktów, potem id. */
async function seedSourceFor(storeId: Id): Promise<{ target: Id; model: StoreModel } | null> {
  const [stores, chains] = await Promise.all([db.stores.toArray(), db.chains.toArray()]);
  const resolveStore = resolveWith(buildMergeMap(stores));
  const resolveChain = resolveWith(buildMergeMap(chains));
  const target = resolveStore(storeId);
  const store = stores.find((s) => s.id === target);
  if (!store || store.deleted || !store.chainId) return null;
  const chainId = resolveChain(store.chainId);
  if ((await loadStoreModel(target)).eventCount > 0) return null;

  const siblings = stores.filter(
    (s) => !s.deleted && !s.mergedInto && s.id !== target && resolveStore(s.id) !== target && s.chainId && resolveChain(s.chainId) === chainId,
  );
  if (siblings.length === 0) return null;
  const visits = new Map<Id, number>();
  for (const v of await db.visits.toArray()) {
    if (v.deleted) continue;
    const id = resolveStore(v.storeId);
    visits.set(id, (visits.get(id) ?? 0) + 1);
  }
  let best: { model: StoreModel; visits: number; confirmed: number } | null = null;
  for (const s of siblings) {
    const model = await loadStoreModel(s.id);
    const confirmed = confirmedCount(model);
    if (confirmed < MIN_KNOWN) continue;
    const n = visits.get(s.id) ?? 0;
    const better =
      !best ||
      n > best.visits ||
      (n === best.visits && (confirmed > best.confirmed || (confirmed === best.confirmed && s.id < best.model.storeId)));
    if (better) best = { model, visits: n, confirmed };
  }
  return best ? { target, model: best.model } : null;
}

export interface RouteSize {
  /** Produkty potwierdzone wizytą — tyle da się skopiować. */
  confirmed: number;
  /** Wszystkie pozycje trasy (z kopiami i zgadniętymi). */
  total: number;
  visits: number;
}

/** Wielkość trasy i liczba wizyt każdego sklepu (po scaleniach) — do wyboru sklepu, z którego kopiujemy. */
export function useRouteSizes(enabled: boolean): Map<Id, RouteSize> | undefined {
  return useLiveQuery(async () => {
    if (!enabled) return undefined;
    const [stores, products, events, visits] = await Promise.all([
      db.stores.toArray(),
      db.products.toArray(),
      db.orderEvents.toArray(),
      db.visits.toArray(),
    ]);
    const resolveStore = resolveWith(buildMergeMap(stores));
    const resolveProduct = resolveWith(buildMergeMap(products));
    const byStore = new Map<Id, OrderEvent[]>();
    for (const e of events as unknown as OrderEvent[]) {
      const id = resolveStore(e.storeId);
      const list = byStore.get(id);
      if (list) list.push(e);
      else byStore.set(id, [e]);
    }
    const out = new Map<Id, RouteSize>();
    const entry = (id: Id) => {
      let e = out.get(id);
      if (!e) out.set(id, (e = { confirmed: 0, total: 0, visits: 0 }));
      return e;
    };
    for (const [id, log] of byStore) {
      const model = foldStore(id, log, { resolveProduct, resolveStore });
      const e = entry(id);
      e.confirmed = confirmedCount(model);
      e.total = Object.keys(model.byProduct).length;
    }
    for (const v of visits) if (!v.deleted) entry(resolveStore(v.storeId)).visits++;
    return out;
  }, [enabled]);
}

/**
 * Ręczna kopia trasy innego sklepu („Skopiuj trasę z innego sklepu…”): obecna trasa jest zastępowana
 * (reset + seeded w jednym zapisie). Zwraca liczbę skopiowanych produktów; 0 = źródło nie ma trasy (nic nie zmieniono).
 */
export async function copyRoute(targetStoreId: Id, sourceStoreId: Id): Promise<number> {
  const resolveStore = resolveWith(buildMergeMap(await db.stores.toArray()));
  const target = resolveStore(targetStoreId);
  const source = resolveStore(sourceStoreId);
  if (target === source) return 0;
  const snapshot = snapshotOf(await loadStoreModel(source));
  if (snapshot.length === 0) return 0;
  await commit(async (w) => {
    await w.append({ storeId: target, kind: 'reset', deviceId: getDeviceId() });
    await w.append({ storeId: target, kind: 'seeded', fromStoreId: source, snapshot, deviceId: getDeviceId() });
  });
  await forgetPlacementTries(target);
  return snapshot.length;
}
